/**
 * Bridge to the legacy stats page runtime — `window.StatPage` and the page-load globals
 * beside it.
 *
 * Everything here reaches into live page state or fires an imperative page command, so it
 * cannot be replaced by an import — but it should be reached through this module rather than
 * off the global directly. One place to stub in tests, one place to delete when the page
 * shell itself migrates.
 *
 * Pure helpers do NOT belong here. If a function only transforms its arguments, port it to
 * the React libs (`lib/helpers.js`, `page/components/lib/utils.js`) and import it instead.
 */

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { getIsDefaultReportParams } from '@aytm/app/stats/page/comments/lib/utils'

const page = () => window.StatPage

// --- The active report ---------------------------------------------------------------------
// Which report is open, what class it is, and the params it was opened with.

export const getReportId = () => page()?.model?.getReportId?.()

export const getOriginalReportId = () => page()?.lib?.getOriginalReport?.()?.id

export const isReportXpert = () => page()?.model?.isReportXpert?.()

export const isReportVirtual = () => page()?.model?.isReportVirtual?.()

export const getReportConfig = () => page()?.model?.getReportConfig?.()

// The report-param sections a surface reads its own state from, keyed by param name.
export const getReportParams = () => page()?.model?.getReportParams?.() || {}

// A report param as it stands in the store, which lags the page-model attribute the change events
// fire on - the difference is how the cloud knows its own config moved.
export const getStoredReportParams = () => page()?.model?.getReportParams?.(null, false) || {}

// Report-local (virtual) variables — derived variables the report adds on top of the survey.
export const getReportVirtual = () => page()?.model?.getReportVirtual?.() || {}

// The codes and codebooks a report's questions are built from, its local changes included. Asked by id:
// the sidebar's report entries carry this record for the open report only.
export const getReportLogicData = reportId => page()?.model?.getReportLogicData?.(reportId) || []

// An Xpert question is drawn from the chart block of the info view depending on it; which block
// that currently is, is the report's to resolve.
export const getReportCurrentXQuestion = question => page()?.model?.getReportCurrentXQuestion?.(question)

// A Survey Guest, a virtual report and the like keep no local changes -- and so no pinned templates either.
export const isReportStorageDisabled = () => Boolean(page()?.model?.disableReportLocalStorage?.())

// `chartMode` is the one key a block must not read raw: `auto` is the absence of a mode, and
// PageModel resolves it to the page default before a block ever sees it.
export const getModelValue = key =>
  'chartMode' === key ? page()?.model?.globalChartMode?.() ?? page()?.model?.get?.(key) : page()?.model?.get?.(key)

// --- Features ------------------------------------------------------------------------------
// The report-mode-aware registry; nothing reads the page-load globals directly.

// Everything the account was served, plus the flags this report mode derives on top. The page
// model rebuilds it whenever either half can have moved -- a report or mode switch, and the
// mid-session merge that rewrites the payload -- so it is read at call time, never captured.
export const getFeature = token => page()?.model?.features?.[token]

export const getFeatures = () => page()?.model?.features || {}

export const isFeatureDisabled = token => page()?.model?.disabledFeature?.(token)

export const isGuest = () => Boolean(window?.userRoles?.includes?.('guest'))

// --- Appearance ----------------------------------------------------------------------------
// Palette, theme, images, and the width a question is given to draw in.

// The active palette; the gradient stops and the value-to-colour scale are pure and live in
// lib/colors.js instead.
export const getColorTheme = () => page()?.model?.colorTheme?.() || []

export const getColorThemes = () => page()?.lib?.colorThemes

// Page-load cloud collections: the palettes the report offers and the presets the user saved.
export const getCustomPalettes = () => window?.customPalettes

export const getWcCustomPresets = () => window?.wc_custom_presets

export const setWcCustomPresets = presets => (window.wc_custom_presets = structuredClone(presets || []))

// The custom presets the report offers just changed; every cloud on the page rebuilds its list.
export const triggerPresetsUpdate = () => page()?.model?.trigger?.('presetsUpdate')

// A report opened through a share link, which cannot save anything back to it.

// The width the report gives a question to draw in - a computed value, not a stored attribute.
export const getWidth = () => page()?.model?.width?.()

export const getImage = image => page()?.model?.getImage?.(image)

// --- Questions and their positions ---------------------------------------------------------
// Identity, ordering and text of the questions the report draws.

// How many questions the report draws. This is the page's own survey collection, not the
// `window.survey` page-load globals further down.
export const getQuestionCount = () => page()?.survey?.models?.length ?? 0

// The id the export services address a question by, composed from the parts of a block key: the
// question, its sub-question or answer variant, and the heatmap cluster it was read under.
// Resolves a question's id *within a report*, through that report's own virtual questions. Named
// apart from the `getQuestionId` that builds a question's own id from the question itself -- they
// take different things and answer different questions.
export const getReportQuestionId = (reportId, parts, withDots = true) =>
  page()?.model?.getQuestionId?.(reportId, parts, withDots)

// The id/position conversions are not here: the page keeps its own for its own use, and the React
// side has its own in `page/components/lib/utils`. Two implementations of one idea under one name
// is how they came to disagree about which number an id spells.

// Question texts are stored with display logic and alias markup; the page strips them per the
// report's visibility settings.
export const cloneQuestionAndProcessTexts = (question, options) =>
  page()?.model?.cloneQuestionAndProcessTexts?.(question, options)


// --- Traits, variables and tags ------------------------------------------------------------
// The dimensions a report can be split or filtered by.

export const getTrait = () => page()?.model?.getTrait?.()

export const getTraits = () => page()?.lib?.getTraits?.() || []

export const getTagsList = () => page()?.lib?.getTagsList?.()

export const getGeoTraits = () => page()?.lib?.getGeoTraits?.() || []

export const getCustomVariables = () => page()?.lib?.getCustomVariables?.() || {}

// Every tag the report can offer, across questions - the verbatim controls pick from it.

// --- The survey behind the report ----------------------------------------------------------
// Page-load survey state: its schema, its status and its assets.

export const getSurveyTitle = () => window?.survey?.title

// Whether the survey is still collecting, which is why a report over it can read as incomplete.
export const isSurveyStarted = () => 'started' === window?.survey?.status

export const getSurveyOptions = () => window?.survey?.options || {}

// Per-question-type engine config; an answer carries its own once the report was run with it.
export const getSurveyConfig = type => window?.survey?.config?.[type]

// Survey-level variable collections ('variables' / 'pipingVariables'), keyed by variable name.
export const getSurveyVariables = type => window?.survey?.[type] || {}

// The survey's coding books, each carrying the colour its tags are drawn in.
export const getSurveyCodeBooks = () => window?.survey?.codeBooks || []

// The smile scales a smiley-scale question draws its answers with, one set per sub-type and length.

// Upper-cased references of everything the survey can pipe from; a variable missing here is
// not addressable in a report.
export const getSurveyRefsList = () => window?.survey?.refsList || {}

// Page-load identity globals, stamped into export payloads beside the filters string.

// The asset collection is page-load state on the survey global, not on the page model.
export const getSurveyAssets = assetType => window?.survey?.assets?.[assetType] || []

export const updateSurveyAsset = (assetType, assets) => page()?.api?.updateSurveyAsset?.(assetType, assets)

export const deleteSurveyAsset = (assetType, uid) => page()?.api?.deleteSurveyAsset?.(assetType, uid)

// --- Page commands -------------------------------------------------------------------------
// Imperative gestures the page owns; each one re-renders something.

export const scrollToQuestion = id => page()?.view?.scrollToQuestion?.(id)

/**
 * Opens the comments panel on a question, optionally narrowed by a query (batch, cluster,
 * sentiment or tag segments). Shared by every surface that offers a comments affordance.
 *
 * The reroute branch mirrors the page's own behaviour: an Xpert report viewed without a share
 * key writes against the original report rather than the virtual one, and starts from empty
 * params instead of the current report's.
 */
// Scrolls the report to a question, optionally to one of its batches.
export const scrollToQuestionOriginal = (id, batch) => page()?.view?.scrollToQuestionOriginal?.(id, batch)

export const openCommentsPanel = ({ event, path, query = [], isReportXpert = false }) => {
  const reroute = undefined === PageGlobals.SHARE_KEY && isReportXpert
  const comments = Object.assign({}, (!reroute && getModelValue('comments')) || {})

  comments.query = query
  comments.selectedQuestion = path

  const reportId = reroute ? getOriginalReportId() : getReportId()
  // A params set that matches the defaults is written as null, so the panel drops the section
  // rather than persisting a no-op.
  const updateSection = { comments: (!getIsDefaultReportParams(comments) && comments) || null }

  page()?.view?.onUpdateParams?.(event, updateSection, reportId, 'comments')
}

// --- The report-data job -------------------------------------------------------------------
// Running a report and reading its answers back.

export const getApiKey = () => getModelValue('apiKey')

export const isApiKeyInvalid = () => page()?.model?.isApiKeyInvalid?.()

// The key is refreshed out-of-band; both forms take the callback the caller wants re-run with it.
export const fetchApiKey = options => page()?.bridge?.fetchApiKey?.(options)

export const delayFetchApiKey = options => page()?.bridge?.delayFetchApiKey?.(options)

export const getQueryUrl = () => page()?.lib?.queryUrl

// Asset endpoints are addressed per page mode, so the mode read and the url build stay paired.
export const getKeyUrl = () => page()?.lib?.getKeyUrl?.(getModelValue('mode'))

// v2 fetches a question in two hops (index for the hashes, items for the data); the legacy path
// posts one aggregate query.
export const useStatsV2 = () => Boolean(page()?.api?.useStatsV2?.())

export const callReportIndex = (...args) => page()?.api?.callReportIndex?.(...args)

export const callReportItems = (...args) => page()?.api?.callReportItems?.(...args)

export const adaptItemsResponse = response => page()?.api?._adaptItemsResponse?.(response) || {}

export const loadQuestionsSuccess = (...args) => page()?.api?.loadQuestionsSuccess?.()?.(...args)

export const loadQuestionSuccess = (schemaKey, ...args) => page()?.api?.loadQuestionSuccess?.(schemaKey)?.(...args)

export const loadQuestionsError = () => page()?.api?.loadQuestionsError?.()

// --- Export --------------------------------------------------------------------------------
// What the export services are handed.

// The export panel lists what each question can offer, which a finished run changes.
export const renderExportProps = (force = true) => page()?.sidebar?.renderExportProps?.(force)

export const downloadExportFile = (...args) => page()?.classes?.Export?.Download?.downloadExportFile?.(...args)

// Human-readable summary of the active filters, stamped into export payloads.
export const humanizedFiltersString = () => page()?.model?.humanizedFiltersString?.()
